import React, { useState, useEffect } from 'react';
import { Search, Edit2, Save, X, Plus, Trash2, Eye, EyeOff, Package, AlertTriangle } from 'lucide-react';


import {
    updateProduct,
    getAllProducts,

    deleteProduct,
    activateProduct,
    getLowStockProducts,
    fetchProductsWithPagination
} from '../../../../services/apiProduct';

const ProductsManager = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        isActive: 'all',
        unitOfMeasure: 'all',
        lowStock: false
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        totalPages: 1,
        totalCount: 0
    });

    const unitOptions = ['pieces', 'kg', 'grams', 'liters', 'meters', 'boxes'];

    useEffect(() => {
        fetchProducts();
    }, [searchTerm, filters, pagination.page]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm,
                ...(filters.isActive !== 'all' && { isActive: filters.isActive === 'true' }),
                ...(filters.unitOfMeasure !== 'all' && { unitOfMeasure: filters.unitOfMeasure })
            };

            let response;

            if (filters.lowStock) {
                // Use the specialized low stock API
                response = await getLowStockProducts(20);
            } else {
                // Use the main products API with pagination
                response = await fetchProductsWithPagination(
                    pagination.page,
                    pagination.limit,
                    'createdAt',
                    'desc',
                    params
                );
            }

            // Handle different response structures
            const productsList = response.products || response.data || response;
            const isArray = Array.isArray(productsList);

            setProducts(isArray ? productsList : []);
            setPagination(prev => ({
                ...prev,
                totalPages: response.totalPages || Math.ceil((response.totalCount || productsList.length) / pagination.limit),
                totalCount: response.totalCount || productsList.length
            }));
        } catch (err) {
            setError(typeof err === 'string' ? err : err.message || 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (product) => {
        setEditingId(product._id);
        setEditForm({
            name: product.name,
            sku: product.sku,
            description: product.description || '',
            unitOfMeasure: product.unitOfMeasure,
            costPrice: product.costPrice,
            sellingPrice: product.sellingPrice,
            currentStock: product.currentStock,
            isActive: product.isActive
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSaveEdit = async () => {
        try {
            const response = await updateProduct(editingId, editForm);

            // Update local state
            setProducts(products.map(product =>
                product._id === editingId
                    ? { ...product, ...editForm }
                    : product
            ));

            setEditingId(null);
            setEditForm({});

            // Show success message if needed
            console.log('Product updated successfully:', response);
        } catch (err) {
            setError(typeof err === 'string' ? err : err.message || 'Failed to update product');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                const response = await deleteProduct(id);
                setProducts(products.filter(product => product._id !== id));
                console.log('Product deleted successfully:', response);
            } catch (err) {
                setError(typeof err === 'string' ? err : err.message || 'Failed to delete product');
            }
        }
    };

    const handleToggleStatus = async (product) => {
        try {
            if (!product.isActive) {
                const response = await activateProduct(product._id);
                console.log('Product activated:', response);
            } else {
                // Deactivate product by updating isActive to false
                const response = await updateProduct(product._id, { isActive: false });
                console.log('Product deactivated:', response);
            }

            setProducts(products.map(p =>
                p._id === product._id
                    ? { ...p, isActive: !p.isActive }
                    : p
            ));
        } catch (err) {
            setError(typeof err === 'string' ? err : err.message || 'Failed to update product status');
        }
    };

    const handleInputChange = (field, value) => {
        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStockStatus = (stock) => {
        if (stock === 0) return { color: 'text-red-600', label: 'Out of Stock' };
        if (stock <= 10) return { color: 'text-orange-600', label: 'Low Stock' };
        return { color: 'text-green-600', label: 'In Stock' };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Package className="h-6 w-6 text-blue-600" />
                    <h1 className="text-2xl font-bold text-gray-900">Products Management</h1>
                </div>
                <p className="text-gray-600">Manage your product inventory, prices, and stock levels</p>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    <span className="text-red-700">{error}</span>
                    <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Search and Filters */}
            <div className="mb-6 space-y-4">
                {/* Search Bar */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                        type="text"
                        placeholder="Search products by name, SKU, or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Filters */}
                <div className="flex flex-wrap gap-4">
                    <select
                        value={filters.isActive}
                        onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Status</option>
                        <option value="true">Active Only</option>
                        <option value="false">Inactive Only</option>
                    </select>

                    <select
                        value={filters.unitOfMeasure}
                        onChange={(e) => setFilters(prev => ({ ...prev, unitOfMeasure: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Units</option>
                        {unitOptions.map(unit => (
                            <option key={unit} value={unit}>{unit}</option>
                        ))}
                    </select>

                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={filters.lowStock}
                            onChange={(e) => setFilters(prev => ({ ...prev, lowStock: e.target.checked }))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Low Stock Only</span>
                    </label>
                </div>
            </div>

            {/* Products Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Selling Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {products.map((product) => (
                                <tr key={product._id} className="hover:bg-gray-50">
                                    {editingId === product._id ? (
                                        // Edit Mode
                                        <>
                                            <td className="px-6 py-4">
                                                <div className="space-y-2">
                                                    <input
                                                        type="text"
                                                        value={editForm.name}
                                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                                        className="w-full p-1 border rounded text-sm font-medium"
                                                        placeholder="Product name"
                                                    />
                                                    <textarea
                                                        value={editForm.description}
                                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                                        className="w-full p-1 border rounded text-xs text-gray-600"
                                                        placeholder="Description"
                                                        rows="2"
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="text"
                                                    value={editForm.sku}
                                                    onChange={(e) => handleInputChange('sku', e.target.value.toUpperCase())}
                                                    className="w-full p-1 border rounded text-sm font-mono"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={editForm.unitOfMeasure}
                                                    onChange={(e) => handleInputChange('unitOfMeasure', e.target.value)}
                                                    className="w-full p-1 border rounded text-sm"
                                                >
                                                    {unitOptions.map(unit => (
                                                        <option key={unit} value={unit}>{unit}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    value={editForm.costPrice}
                                                    onChange={(e) => handleInputChange('costPrice', parseFloat(e.target.value) || 0)}
                                                    className="w-full p-1 border rounded text-sm"
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    value={editForm.sellingPrice}
                                                    onChange={(e) => handleInputChange('sellingPrice', parseFloat(e.target.value) || 0)}
                                                    className="w-full p-1 border rounded text-sm"
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <input
                                                    type="number"
                                                    value={editForm.currentStock}
                                                    onChange={(e) => handleInputChange('currentStock', parseInt(e.target.value) || 0)}
                                                    className="w-full p-1 border rounded text-sm"
                                                    min="0"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <label className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isActive}
                                                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="ml-2 text-sm">Active</span>
                                                </label>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleSaveEdit}
                                                        className="p-1 text-green-600 hover:text-green-800"
                                                        title="Save changes"
                                                    >
                                                        <Save className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={handleCancelEdit}
                                                        className="p-1 text-gray-600 hover:text-gray-800"
                                                        title="Cancel edit"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        // Display Mode
                                        <>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                    {product.description && (
                                                        <div className="text-xs text-gray-500 mt-1">{product.description}</div>
                                                    )}
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        Supplier: {product.supplier?.name || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-mono text-gray-900">{product.sku}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-900 capitalize">{product.unitOfMeasure}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-900">{formatCurrency(product.costPrice)}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm font-medium text-gray-900">{formatCurrency(product.sellingPrice)}</span>
                                                <div className="text-xs text-gray-500">
                                                    Margin: {((product.sellingPrice - product.costPrice) / product.costPrice * 100).toFixed(1)}%
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <span className={`text-sm font-medium ${getStockStatus(product.currentStock).color}`}>
                                                        {product.currentStock} {product.unitOfMeasure}
                                                    </span>
                                                </div>
                                                <div className={`text-xs ${getStockStatus(product.currentStock).color}`}>
                                                    {getStockStatus(product.currentStock).label}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${product.isActive
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {product.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleEdit(product)}
                                                        className="p-1 text-blue-600 hover:text-blue-800"
                                                        title="Edit product"
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(product)}
                                                        className={`p-1 ${product.isActive ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'}`}
                                                        title={product.isActive ? 'Deactivate product' : 'Activate product'}
                                                    >
                                                        {product.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(product._id)}
                                                        className="p-1 text-red-600 hover:text-red-800"
                                                        title="Delete product"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {products.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <Package className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm || filters.isActive !== 'all' || filters.unitOfMeasure !== 'all'
                                ? 'Try adjusting your search or filters.'
                                : 'Get started by adding your first product.'}
                        </p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of {pagination.totalCount} results
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <span className="px-3 py-2 text-sm bg-blue-50 border border-blue-200 rounded-lg">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsManager;